import { z } from 'zod';

/**
 * Zod schema for meeting minute frontmatter validation
 * Provides runtime validation and type safety
 */
export const MeetingMetadataSchema = z
	.object({
		// Required fields
		title: z.string().min(1, 'Title is required').trim(),
		date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
		type: z.enum(['General Meeting', 'Board Meeting', 'Special Meeting'], {
			message: 'Type must be General Meeting, Board Meeting, or Special Meeting'
		}),
		summary: z.string().min(1, 'Summary is required').trim(),

		// Optional time fields (local time for US/Pacific)
		startTime: z
			.string()
			.regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Start time must be in HH:MM format')
			.optional(),
		endTime: z
			.string()
			.regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'End time must be in HH:MM format')
			.optional(),
		timezone: z.string().default('America/Los_Angeles'),

		// Optional fields with defaults and validation
		published: z.boolean().default(false),
		status: z.enum(['Draft', 'Approved', 'Pending Approval']).optional(),
		attendees: z.number().int().min(0, 'Attendees must be a non-negative integer').optional()
	})
	.strict(); // Prevent additional properties

/**
 * Schema for the processed meeting minute data structure
 */
export const MeetingMinuteSchema = z.object({
	slug: z.string().regex(/^\/meetings\/\d{4}\/[a-z0-9-]+$/, 'Invalid slug format'),
	title: z.string().min(1),
	date: z.date(),
	type: z.enum(['General Meeting', 'Board Meeting', 'Special Meeting']),
	summary: z.string().optional(),
	startTime: z.string().optional(),
	endTime: z.string().optional(),
	timezone: z.string().default('America/Los_Angeles'),
	published: z.boolean().default(false),
	status: z.enum(['Draft', 'Approved', 'Pending Approval']).optional(),
	attendees: z.number().int().min(0).optional()
});

/**
 * Path validation schema for security
 */
export const MeetingPathSchema = z.object({
	year: z
		.string()
		.regex(/^\d{4}$/)
		.refine((year) => {
			const yearNum = parseInt(year);
			const currentYear = new Date().getFullYear();
			return yearNum >= 2000 && yearNum <= currentYear + 10;
		}, 'Year must be between 2000 and 10 years in the future'),
	slug: z
		.string()
		.regex(/^[a-z0-9-]+$/, 'Slug must contain only lowercase letters, numbers, and hyphens')
});

// Infer TypeScript types from Zod schemas
export type MeetingMetadata = z.infer<typeof MeetingMetadataSchema>;
export type MeetingMinute = z.infer<typeof MeetingMinuteSchema>;
export type MeetingPath = z.infer<typeof MeetingPathSchema>;

/**
 * Transform metadata to meeting minute format with validation
 */
export function transformToMeetingMinute(
	metadata: unknown,
	pathInfo: MeetingPath
): MeetingMinute | null {
	try {
		// Validate and parse metadata
		const validMetadata = MeetingMetadataSchema.parse(metadata);

		// Transform to MeetingMinute format
		const meetingMinute = {
			slug: `/meetings/${pathInfo.year}/${pathInfo.slug}`,
			title: validMetadata.title,
			date: new Date(validMetadata.date + 'T00:00:00'),
			type: validMetadata.type,
			summary: validMetadata.summary,
			startTime: validMetadata.startTime,
			endTime: validMetadata.endTime,
			timezone: validMetadata.timezone,
			published: validMetadata.published,
			status: validMetadata.status,
			attendees: validMetadata.attendees
		};

		// Validate the final result
		return MeetingMinuteSchema.parse(meetingMinute);
	} catch (error) {
		if (error instanceof z.ZodError) {
			console.warn('Meeting metadata validation failed:', {
				path: `/meetings/${pathInfo.year}/${pathInfo.slug}`,
				errors: error.issues.map((e) => `${e.path.join('.')}: ${e.message}`)
			});
		} else {
			console.warn('Unexpected error transforming meeting data:', error);
		}
		return null;
	}
}
